import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
}

export default function Pagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
}: PaginationProps) {
  // Don't show pagination if there's only one page or no items
  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage > 3) {
        pages.push("...");
      }

      // Show pages around current page
      const startPage = Math.max(2, currentPage - 1);
      const endPage = Math.min(totalPages - 1, currentPage + 1);

      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 2) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <>
      {/* Desktop Pagination */}
      <div className="hidden lg:flex items-center justify-between px-6 py-4 bg-white border-t border-gray-200">
        {/* Items info */}
        <div className="text-sm text-gray-700">
          แสดง <span className="font-medium">{startItem}</span> ถึง{" "}
          <span className="font-medium">{endItem}</span> จาก{" "}
          <span className="font-medium">{totalItems}</span> รายการ
        </div>

        {/* Pagination controls */}
        <div className="flex items-center gap-2">
          {/* Previous button */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              currentPage === 1
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            }`}
          >
            <ChevronLeft size={16} />
            ก่อนหน้า
          </button>

          {/* Page numbers */}
          <div className="flex gap-1">
            {getPageNumbers().map((page, index) => {
              if (page === "...") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="px-3 py-2 text-sm text-gray-500"
                  >
                    ...
                  </span>
                );
              }

              const pageNum = page as number;
              const isActive = pageNum === currentPage;

              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? "bg-emerald-600 text-white"
                      : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          {/* Next button */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
              currentPage === totalPages
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            }`}
          >
            ถัดไป
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Mobile Pagination */}
      <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`touch-target flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg transition-all border ${
            currentPage === 1
              ? "text-gray-400 bg-gray-100 border-gray-100 cursor-not-allowed"
              : "text-gray-700 bg-white border-gray-300 active:bg-gray-50"
          }`}
        >
          <ChevronLeft size={16} />
          ก่อนหน้า
        </button>

        <span className="text-xs font-semibold text-gray-700 font-sans select-none">
          หน้า {currentPage} / {totalPages}
        </span>

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`touch-target flex items-center justify-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg transition-all border ${
            currentPage === totalPages
              ? "text-gray-400 bg-gray-100 border-gray-100 cursor-not-allowed"
              : "text-gray-700 bg-white border-gray-300 active:bg-gray-50"
          }`}
        >
          ถัดไป
          <ChevronRight size={16} />
        </button>
      </div>
    </>
  );
}
